import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { type TTicket } from '../types/ticket.type';

export type TGetSingleTicketResponse = ApiResponse<TTicket>;
export type TGetSingleTicketParams = {
  ticketId: string;
};
export const getSingleTicket = async (params: TGetSingleTicketParams): Promise<TGetSingleTicketResponse> => {
  const url = `/ticket/${params.ticketId}`;

  return axiosInstance.get(url, { params });
};

export const GET_SINGLE_TICKET_QUERY_KEY = 'get-single-ticketing-ticket';

export const getSingleTicketQueryOptions = <TData = TTicket>(
  params: TGetSingleTicketParams,
  select?: (data: TGetSingleTicketResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_SINGLE_TICKET_QUERY_KEY,
      {
        ticketId: params?.ticketId,
      },
    ],
    queryFn: () => getSingleTicket(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useSingleTicketQuery<TData>(options: {
  params: TGetSingleTicketParams;
  queryConfig?: QueryConfig<typeof getSingleTicketQueryOptions<TData>>;
  select: (data: TGetSingleTicketResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useSingleTicketQuery(options: {
  params: TGetSingleTicketParams;
  queryConfig?: QueryConfig<typeof getSingleTicketQueryOptions<TGetSingleTicketResponse>>;
}): ReturnType<typeof useQuery<TGetSingleTicketResponse>>;

// Implementation
export function useSingleTicketQuery<TData = TGetSingleTicketResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetSingleTicketParams;
  queryConfig?: QueryConfig<typeof getSingleTicketQueryOptions<TData | TGetSingleTicketResponse>>;
  select?: (data: TGetSingleTicketResponse) => TData;
}) {
  return useQuery({
    ...getSingleTicketQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetSingleTicketResponse ? TGetSingleTicketResponse : TData>>;
}
